/**
 * Market — 4-slot row + wipe controls + nature-spend buttons.
 *
 * Originally derived from the (removed) board-game-playground for visual parity. UI is NOT shared (CLAUDE.md).
 * Mobile: bottom sheet. Tablet/Desktop: side rail.
 */

import type {
  Market as MarketData,
  TurnPhase,
} from "@cloudnest/cascadia-engine";
import { useState } from "react";
import { MarketSlot } from "./MarketSlot";

export interface MarketProps {
  readonly market: MarketData;
  readonly phase: TurnPhase;
  readonly activePlayerNatureTokens: number;
  readonly selectedSlot: number | null;
  readonly onDraftPair: (slot: number) => void;
  readonly onWipeOptional: () => void;
  readonly onSpendNatureDecouple: () => void;
  readonly layout: "sheet" | "rail";
}

export function Market({
  market,
  phase,
  activePlayerNatureTokens,
  selectedSlot,
  onDraftPair,
  onWipeOptional,
  onSpendNatureDecouple,
  layout,
}: MarketProps) {
  const [sheetExpanded, setSheetExpanded] = useState(false);

  const isMarketPhase = phase === "marketCheck";
  const canWipeOptional =
    isMarketPhase &&
    !market.optionalWipeUsedThisTurn &&
    hasThreeSameWildlife(market);
  const canDecouple =
    isMarketPhase &&
    !market.decoupleUnlockedThisTurn &&
    activePlayerNatureTokens >= 1;

  const slotsRow = (
    <div
      data-testid="cascadia-market-slots"
      style={{
        display: "flex",
        gap: 8,
        padding: "8px 0",
      }}
    >
      {market.slots.map((slot) => (
        <MarketSlot
          key={slot.index}
          slot={slot}
          isSelected={selectedSlot === slot.index}
          isDisabled={!isMarketPhase || market.autoWipePending}
          onTap={onDraftPair}
        />
      ))}
    </div>
  );

  const controls = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {canWipeOptional ? (
        <button
          data-testid="cascadia-wipe-optional-btn"
          data-action-type="WipeMarketOptional"
          onClick={onWipeOptional}
          type="button"
          style={controlButtonStyle}
          aria-label="Optional market wipe — 3 same wildlife"
        >
          시장 청소 / Wipe
        </button>
      ) : null}
      {canDecouple ? (
        <button
          data-testid="cascadia-decouple-btn"
          data-action-type="SpendNatureDecouple"
          onClick={onSpendNatureDecouple}
          type="button"
          style={controlButtonStyle}
          aria-label="Spend nature token to decouple draft"
        >
          자연 분리 / Decouple
        </button>
      ) : null}
    </div>
  );

  if (layout === "rail") {
    return (
      <aside
        data-testid="cascadia-market"
        aria-label="Market"
        style={{
          padding: 12,
          background: "var(--cn-cascadia-market-slot)",
          border: "1px solid var(--cn-cascadia-market-slot-border)",
          borderRadius: 8,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--cn-cascadia-label)",
            marginBottom: 8,
          }}
        >
          MARKET — 시장
        </div>
        {slotsRow}
        {controls}
      </aside>
    );
  }

  return (
    <section
      data-testid="cascadia-market"
      aria-label="Market"
      style={{
        background: "var(--cn-cascadia-bottom-sheet-bg)",
        borderTop: "1px solid var(--cn-cascadia-market-slot-border)",
        borderRadius: "16px 16px 0 0",
      }}
    >
      <button
        data-testid="cascadia-market-handle"
        onClick={() => setSheetExpanded((v) => !v)}
        type="button"
        aria-label={sheetExpanded ? "Collapse market" : "Expand market"}
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "8px 0",
          cursor: "pointer",
          minHeight: 32,
          width: "100%",
          background: "none",
          border: "none",
        }}
      >
        <div
          style={{
            width: 40,
            height: 4,
            background: "var(--cn-cascadia-bottom-sheet-handle)",
            borderRadius: 2,
          }}
        />
      </button>

      <div style={{ padding: "0 12px 8px" }}>
        {slotsRow}
        {sheetExpanded ? <div style={{ paddingTop: 8 }}>{controls}</div> : null}
      </div>
    </section>
  );
}

const controlButtonStyle: React.CSSProperties = {
  padding: "8px 12px",
  minHeight: 44,
  borderRadius: 6,
  border: "1px solid var(--cn-cascadia-market-slot-border)",
  background: "var(--cn-cascadia-tile-bg)",
  cursor: "pointer",
  fontSize: 12,
  color: "var(--cn-cascadia-label)",
  fontWeight: 600,
};

function hasThreeSameWildlife(market: MarketData): boolean {
  const counts: Record<string, number> = {};
  for (const slot of market.slots) {
    if (slot.wildlifeSpecies === null) continue;
    const sp = slot.wildlifeSpecies;
    counts[sp] = (counts[sp] ?? 0) + 1;
    if ((counts[sp] ?? 0) >= 3) return true;
  }
  return false;
}
